/**
 * Public (anonymous) booking API.
 * Uses plain fetch without auth headers so that no login redirect or token refresh is triggered.
 */

import { getApiBaseUrl } from '../../utils/apiClient';

export interface PublicVariant {
  id: string;
  name: string;
  duration_minutes: number;
  price: string | null;
}

export interface PublicResource {
  id: string;
  name: string;
}

export interface PublicService {
  id: string;
  name: string;
  description: string;
  duration_minutes: number;
  price: string | null;
  variants: PublicVariant[];
  resources: PublicResource[];
}

export interface PublicBookingPage {
  name: string;
  booking_page_slug: string;
  timezone: string;
  slot_granularity_minutes: number;
  min_lead_time_minutes: number;
  max_advance_days: number;
  services: PublicService[];
  resources: PublicResource[];
}

export interface PublicSlot {
  start: string;
  end: string;
  resources: PublicResource[];
}

export interface PublicSlotsResponse {
  timezone: string;
  slots: PublicSlot[];
}

export interface PublicBooking {
  manage_token: string;
  status: string;
  profile_name: string;
  timezone: string;
  service_name: string;
  variant_name: string | null;
  resource_name: string | null;
  start: string;
  end: string;
  customer_name: string;
  can_cancel: boolean;
  can_reschedule: boolean;
  booking_page_url: string | null;
}

export interface CreatePublicBookingInput {
  service: string;
  variant?: string;
  resource?: string;
  start: string;
  customer_name: string;
  customer_phone: string;
  customer_email?: string;
  language: 'de' | 'en';
}

export interface SlotQuery {
  service: string;
  variant?: string;
  resource?: string;
  dateFrom?: string;
  days?: number;
}

export class PublicApiError extends Error {
  status: number;
  fieldErrors: Record<string, string>;
  // Stable reason code from the API (e.g. "start_in_past"), if it sent one.
  code?: string;

  constructor(status: number, fieldErrors: Record<string, string> = {}, code?: string) {
    super(`Public API error ${status}`);
    this.name = 'PublicApiError';
    this.status = status;
    this.fieldErrors = fieldErrors;
    this.code = code;
  }
}

const flattenFieldErrors = (raw: unknown): Record<string, string> => {
  const result: Record<string, string> = {};
  if (!raw || typeof raw !== 'object') return result;
  for (const [field, value] of Object.entries(raw as Record<string, unknown>)) {
    const first = Array.isArray(value) ? value[0] : value;
    if (typeof first === 'string') result[field] = first;
  }
  return result;
};

const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
  let response: Response;
  try {
    response = await fetch(`${getApiBaseUrl()}/calendars/public/${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    });
  } catch {
    throw new PublicApiError(0);
  }

  if (!response.ok) {
    let fieldErrors: Record<string, string> = {};
    let code: string | undefined;
    try {
      const body = await response.json();
      fieldErrors = flattenFieldErrors(body?.field_errors);
      code = typeof body?.code === 'string' ? body.code : undefined;
    } catch {
      // Non-JSON error body: the status code alone is enough
    }
    throw new PublicApiError(response.status, fieldErrors, code);
  }

  return (await response.json()) as T;
};

const encodeId = (value: string) => encodeURIComponent(value);

export const publicBookingService = {
  getBookingPage(slug: string): Promise<PublicBookingPage> {
    return request<PublicBookingPage>(`booking-pages/${encodeId(slug)}/`);
  },

  getSlots(slug: string, query: SlotQuery): Promise<PublicSlotsResponse> {
    const params = new URLSearchParams({ service: query.service });
    if (query.variant) params.set('variant', query.variant);
    if (query.resource) params.set('resource', query.resource);
    if (query.dateFrom) params.set('date_from', query.dateFrom);
    if (query.days) params.set('days', String(query.days));
    return request<PublicSlotsResponse>(`booking-pages/${encodeId(slug)}/slots/?${params}`);
  },

  createBooking(slug: string, input: CreatePublicBookingInput): Promise<PublicBooking> {
    return request<PublicBooking>(`booking-pages/${encodeId(slug)}/bookings/`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  getBooking(token: string): Promise<PublicBooking> {
    return request<PublicBooking>(`bookings/${encodeId(token)}/`);
  },

  cancelBooking(token: string): Promise<PublicBooking> {
    return request<PublicBooking>(`bookings/${encodeId(token)}/cancel/`, { method: 'POST' });
  },

  /** Free slots for moving this booking; its own time counts as free. */
  getManageSlots(
    token: string,
    query: { dateFrom?: string; days?: number } = {}
  ): Promise<PublicSlotsResponse> {
    const params = new URLSearchParams();
    if (query.dateFrom) params.set('date_from', query.dateFrom);
    if (query.days) params.set('days', String(query.days));
    return request<PublicSlotsResponse>(`bookings/${encodeId(token)}/slots/?${params}`);
  },

  rescheduleBooking(token: string, start: string, resource?: string): Promise<PublicBooking> {
    return request<PublicBooking>(`bookings/${encodeId(token)}/reschedule/`, {
      method: 'POST',
      body: JSON.stringify({ start, ...(resource ? { resource } : {}) }),
    });
  },
};

/** Formats an ISO timestamp in the booking's timezone, not the browser's. */
export const formatInTimezone = (
  iso: string,
  timeZone: string,
  locale: string,
  options: Intl.DateTimeFormatOptions
): string => new Intl.DateTimeFormat(locale, { timeZone, ...options }).format(new Date(iso));
